import { motion } from 'framer-motion'
import { Camera, Plane, Activity, Sparkles, DollarSign } from 'lucide-react'
import { Button } from '@/components/ui/button-partners'

const beneficios = [
  {
    icon: Plane,
    title: 'Agências de viagem e experiências',
    points: [
      'Prolonga a experiência da viagem depois do retorno',
      'Cria um álbum que o cliente mostra para outras pessoas, despertando interesse em novas viagens',
      'Estimula o desejo de viver novas experiências com a mesma agência',
      'Fortalece a lembrança positiva do serviço oferecido',
    ],
  },
  {
    icon: Camera,
    title: 'Fotógrafos e Estúdios',
    points: [
      'Valoriza o resultado final das fotos, que ganham outra presença quando impressas em um álbum bem acabado',
      'Eleva a percepção do trabalho entregue, indo além do arquivo digital',
      'Cria um material que o cliente mostra para amigos e família, gerando novas indicações',
      'Estimula o cliente a voltar para novos ensaios e novos momentos',
    ],
  },
  {
    icon: Activity,
    title: 'Assessorias de corrida, esporte e bem-estar',
    points: [
      'Transforma conquistas em lembranças físicas, não apenas registros digitais',
      'Motiva alunos a participarem de novos desafios e provas',
      'Reforça o vínculo com a assessoria ao marcar cada etapa da evolução',
      'Incentiva a continuidade e a participação em novas experiências',
    ],
  },
  {
    icon: Sparkles,
    title: 'Para qualquer experiência entregue ao cliente',
    points: [
      'Cria um material físico que mantém a experiência viva no dia a dia',
      'Facilita o compartilhamento da história com outras pessoas',
      'Aumenta a chance de recompra ou novas experiências',
      'Fortalece a relação entre quem entrega a experiência e quem a viveu',
    ],
  },
]

const parceriaBeneficios = [
  'Cada indicação realizada gera uma participação sobre as vendas concluídas',
  'O parceiro é reconhecido sempre que sua indicação resulta em um álbum vendido',
  'O processo é automático, sem burocracia ou gestão manual',
  'A monetização acontece como consequência natural de compartilhar algo que agrega valor à experiência do cliente',
]

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
}

const BeneficiosSection = () => {
  return (
    <section id="beneficios" className="w-full py-20 md:py-28 bg-background">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold italic text-foreground mb-6">
            Benefícios
          </h2>
          <p className="font-serif text-xl md:text-2xl text-foreground mb-4">
            Transforme experiências em valor para seus clientes — e para o seu
            negócio.
          </p>
          <p className="font-body text-base md:text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Além de indicar álbuns que encantam e valorizam suas fotos ou
            serviços, você também recebe uma participação sobre cada venda, de
            forma simples e automática.
          </p>
        </motion.div>

        {/* Partnership Benefits - Highlighted Block (FIRST) */}
        <motion.div
          className="p-8 md:p-10 bg-primary rounded-xl border border-primary mb-10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            <h3 className="font-sans text-lg md:text-xl font-semibold text-white uppercase tracking-wide">
              Participação sobre a venda de álbuns
            </h3>
          </div>

          <p className="text-base text-white/90 leading-relaxed font-body mb-6">
            Além de ampliar a experiência entregue aos seus clientes, a parceria
            também gera retorno financeiro de forma simples e transparente.
          </p>

          <ul className="space-y-3">
            {parceriaBeneficios.map((point, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-white mt-2 flex-shrink-0" />
                <span className="text-sm md:text-base text-white/90 leading-relaxed font-body">
                  {point}
                </span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Benefits Grid - 4 blocks (BELOW partnership) */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
        >
          {beneficios.map(beneficio => (
            <motion.div
              key={beneficio.title}
              variants={itemVariants}
              transition={{ duration: 0.5 }}
              className="p-6 md:p-8 bg-secondary/10 rounded-xl border border-secondary/20"
            >
              <div className="flex items-center gap-4 mb-5">
                <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0">
                  <beneficio.icon className="w-5 h-5 text-secondary" />
                </div>
                <h3 className="font-sans text-base md:text-lg font-semibold text-foreground">
                  {beneficio.title}
                </h3>
              </div>
              <ul className="space-y-3">
                {beneficio.points.map((point, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground leading-relaxed font-body">
                      {point}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Button variant="elegant" size="lg" asChild>
            <a href="/cadastro">Quero ser parceiro</a>
          </Button>
        </motion.div>
      </div>
    </section>
  )
}

export default BeneficiosSection
